/**
 * Financial Results Section - CFO/Board Level Analysis
 * Restructured for strategic financial reading by nature
 */

import React, { useState } from 'react';
import { 
  Landmark, CreditCard, Globe, TrendingUp, TrendingDown, Percent, ArrowLeft, 
  Target, AlertTriangle, CheckCircle, Leaf, Wallet, ArrowUpRight, ArrowDownRight,
  RefreshCw, Zap, DollarSign, Building2, PiggyBank, Repeat, AlertCircle, Info, Clock
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, ReferenceLine, LabelList, CartesianGrid } from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Compact KPI Tooltip
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-64 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

// KPI Card with semantic colors and badges - NO ICON VERSION
const KPICard = ({ 
  title, value, unit, 
  vsBudgetPct, yoyChange, changeUnit = '%', isPP = false, 
  tooltipData, badge, inverse = false 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const displayValue = (unit && (unit.includes('M') || unit === 'M')) 
    ? formatM(value) 
    : value;
  
  // Semantic color logic
  const isBudgetFavorable = inverse 
    ? (vsBudgetPct <= 0) 
    : (vsBudgetPct >= 0);
  const isYoYFavorable = inverse 
    ? (yoyChange <= 0) 
    : (yoyChange >= 0);
  
  const TrendIconBudget = vsBudgetPct >= 0 ? TrendingUp : TrendingDown;
  const TrendIconYoY = yoyChange >= 0 ? TrendingUp : TrendingDown;
  
  // Determine trend direction for corner icon (based on YoY)
  const trendPositive = isYoYFavorable;
  
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible ${tooltipData ? 'cursor-help' : ''}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      
      {/* Trend Arrow Indicator - Corner Icon */}
      <div 
        className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${
          trendPositive ? 'bg-emerald-100' : 'bg-rose-100'
        }`}
      >
        {trendPositive ? (
          <ArrowDownRight className="w-4 h-4 text-emerald-600" />
        ) : (
          <ArrowUpRight className="w-4 h-4 text-rose-600" />
        )}
      </div>
      
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-8">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[11px] text-slate-500 tracking-wider uppercase">{title}</p>
            {badge && (
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${badge.bg} ${badge.text}`}>
                {badge.label}
              </span>
            )}
          </div>
          <p className="font-serif text-2xl font-semibold text-primary">
            {displayValue}<span className="text-sm text-slate-400 ml-1 font-sans">{unit}</span>
          </p>
          
          {/* Comparison Badges - Stacked with arrows */}
          <div className="flex flex-col gap-1 mt-2">
            {vsBudgetPct !== undefined && (
              <span className={`inline-flex items-center gap-1 w-fit text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
                isBudgetFavorable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
              }`}>
                <TrendIconBudget className="w-3 h-3" />
                {vsBudgetPct >= 0 ? '+' : ''}{vsBudgetPct}{isPP ? 'p.p.' : changeUnit} vs Orç.
              </span>
            )}
            {yoyChange !== undefined && (
              <span className={`inline-flex items-center gap-1 w-fit text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
                isYoYFavorable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
              }`}>
                <TrendIconYoY className="w-3 h-3" />
                {yoyChange >= 0 ? '+' : ''}{yoyChange}{isPP ? 'p.p.' : changeUnit} YoY
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Bridge Chart Component
const BridgeChart = ({ data, language }) => {
  let cumulative = 0;
  const processedData = data.map((item, index) => {
    if (item.type === 'start' || item.type === 'end') {
      cumulative = item.value;
      return { ...item, y0: 0, y1: item.value, cumulative: item.value };
    }
    const y0 = cumulative;
    cumulative += item.value;
    return { ...item, y0: Math.min(y0, cumulative), y1: Math.max(y0, cumulative), cumulative };
  });

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={processedData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 10, fill: '#64748b' }}
          interval={0}
          angle={-25}
          textAnchor="end"
          height={60}
        />
        <YAxis 
          tick={{ fontSize: 10, fill: '#64748b' }}
          domain={['auto', 'auto']}
          tickFormatter={(v) => `${v.toFixed(1)}M`}
        />
        <ReferenceLine y={0} stroke="#cbd5e1" strokeWidth={1} />
        <Tooltip 
          formatter={(value, name) => [`${value.toFixed(2)}M USD`, name]}
          contentStyle={{ fontSize: '11px', borderRadius: '8px' }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {processedData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={
                entry.type === 'start' ? '#0A192F' :
                entry.type === 'end' ? '#0A192F' :
                entry.value >= 0 ? '#10b981' : '#ef4444'
              }
            />
          ))}
          <LabelList 
            dataKey="value" 
            position="top" 
            formatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`}
            style={{ fontSize: '10px', fontWeight: '600', fill: '#1e293b' }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const FinancialResults = ({ year, language, setActiveSection, flowMap }) => {
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  // Comprehensive financial data by nature
  const finData = {
    // Total Results
    total2025: -5.9,
    total2024: -3.2,
    budgetTotal: -4.7,
    
    // 1. Bank Loan Interest
    bankInterest: {
      total2025: -3.2,
      total2024: -2.8,
      budget: -3.0,
      byBank: [
        { bank: 'Caixa Geral Angola', v2025: 1.4, v2024: 1.2, rate: 9.2 },
        { bank: 'Banco Valor', v2025: 1.2, v2024: 1.1, rate: 12.0 },
        { bank: 'BAI', v2025: 0.9, v2024: 0, rate: 8.5, isNew: true },
        { bank: 'BCGA / BCS', v2025: 0.7, v2024: 1.5, rate: 7.8 },
      ],
      wacd: 8.5,
      wacdBudget: 7.2,
      wacd2024: 7.2,
    },
    
    // 2. Environmental Provision Unwind - DADOS DETALHADOS PARA SECÇÃO BOARD-LEVEL
    unwindProvision: {
      value2025: -0.6,
      value2024: -0.5,
      budget: -0.5,
      provisionBalance: 12.5,
      discountRate: 5.0,
      isNonCash: true,
      // Dados para os 4 KPIs da secção de Unwind
      custoUnwind: {
        v2025: 0.6,       // M USD (valor absoluto)
        budget2025: 0.5,
        v2024: 0.5,
      },
      taxaAtualizacao: {
        v2025: 5.0,       // %
        budget2025: 4.5,
        v2024: 4.2,
      },
      saldoProvisao: {
        v2025: 11.1,      // M USD
        budget2025: 10.8,
        v2024: 8.5,
      },
      anosRestantes: {
        v2025: 12,        // anos
        budget2025: 10,
        v2024: 10,
      },
      // Drivers da variação do custo unitário
      driversVariacao: {
        custoCombustivel: { impacto: +8, descricao: 'Aumento do custo de combustível para equipamentos de reabilitação' },
        taxaAtualizacao: { impacto: +19, descricao: 'Aumento da taxa de actualização de 4,2% para 5,0%' },
        anosReabilitacao: { impacto: +20, descricao: 'Extensão do prazo de reabilitação de 10 para 12 anos' },
        inflacaoCustos: { impacto: +5, descricao: 'Inflação geral nos custos de materiais e mão-de-obra' },
        revisaoTecnica: { impacto: -12, descricao: 'Revisão técnica do plano reduziu volume estimado de terras a movimentar' },
      },
    },
    
    // 3. Treasury Income
    treasuryIncome: {
      value2025: 0.3,
      value2024: 0.2,
      budget: 0.25,
      avgCashApplied: 28.0,
      avgYield: 1.1,
      byInstrument: [
        { name: 'Depósitos a prazo', value: 0.15 },
        { name: 'Aplicações curto prazo', value: 0.10 },
        { name: 'Outros', value: 0.05 },
      ],
    },
    
    // 4. FX Result
    fxResult: {
      netValue2025: -1.8,
      netValue2024: 0.5,
      budget: -0.5,
      favorable2025: 0.8,
      unfavorable2025: -2.6,
      favorable2024: 1.2,
      unfavorable2024: -0.7,
      byCurrency: [
        { currency: 'USD/AOA', value: -1.2, exposure: 'Dívida' },
        { currency: 'EUR/USD', value: -0.4, exposure: 'Fornecedores' },
        { currency: 'ZAR/USD', value: -0.2, exposure: 'Caixa' },
      ],
      realizedPct: 65,
    },
    
    // 5. Other Financial
    otherFinancial: {
      structuringFee: -0.4,
      bankingServices: -0.2,
      total2025: -0.6,
      total2024: -0.4,
    },
  };

  // Calculate variations
  const totalVsBudgetPct = Math.round((finData.total2025 - finData.budgetTotal) / Math.abs(finData.budgetTotal) * 100);
  const totalYoYPct = Math.round((finData.total2025 - finData.total2024) / Math.abs(finData.total2024) * 100);
  
  const bankIntVsBudget = Math.round((finData.bankInterest.total2025 - finData.bankInterest.budget) / Math.abs(finData.bankInterest.budget) * 100);
  const bankIntYoY = Math.round((finData.bankInterest.total2025 - finData.bankInterest.total2024) / Math.abs(finData.bankInterest.total2024) * 100);
  
  const unwindVsBudget = Math.round((finData.unwindProvision.value2025 - finData.unwindProvision.budget) / Math.abs(finData.unwindProvision.budget) * 100);
  const unwindYoY = Math.round((finData.unwindProvision.value2025 - finData.unwindProvision.value2024) / Math.abs(finData.unwindProvision.value2024) * 100);
  
  const treasuryVsBudget = Math.round((finData.treasuryIncome.value2025 - finData.treasuryIncome.budget) / Math.abs(finData.treasuryIncome.budget) * 100);
  const treasuryYoY = Math.round((finData.treasuryIncome.value2025 - finData.treasuryIncome.value2024) / Math.abs(finData.treasuryIncome.value2024) * 100);
  
  const fxVsBudget = Math.round((finData.fxResult.netValue2025 - finData.fxResult.budget) / Math.abs(finData.fxResult.budget) * 100);
  const fxYoY = finData.fxResult.netValue2024 !== 0 
    ? Math.round((finData.fxResult.netValue2025 - finData.fxResult.netValue2024) / Math.abs(finData.fxResult.netValue2024) * 100)
    : -999;

  // Bridge data
  const bridgeData = [
    { name: '2024', value: finData.total2024, type: 'start' },
    { name: language === 'pt' ? 'Juros Bancários' : 'Bank Interest', value: finData.bankInterest.total2025 - finData.bankInterest.total2024, type: 'change' },
    { name: 'Unwind Ambiental', value: finData.unwindProvision.value2025 - finData.unwindProvision.value2024, type: 'change' },
    { name: language === 'pt' ? 'Juros Tesouraria' : 'Treasury Income', value: finData.treasuryIncome.value2025 - finData.treasuryIncome.value2024, type: 'change' },
    { name: language === 'pt' ? 'Resultado Cambial' : 'FX Result', value: finData.fxResult.netValue2025 - finData.fxResult.netValue2024, type: 'change' },
    { name: language === 'pt' ? 'Outros' : 'Other', value: finData.otherFinancial.total2025 - finData.otherFinancial.total2024, type: 'change' },
    { name: '2025', value: finData.total2025, type: 'end' },
  ];

  return (
    <div className="space-y-6" data-testid="financial-results-section">
      {/* Header */}
      <div>
        <button 
          onClick={() => setActiveSection('income-statement')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Resultados Financeiros' : 'Financial Results'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {language === 'pt' 
                ? `Análise por natureza económica ${comparisonSubtitle}` 
                : `Analysis by economic nature ${comparisonSubtitle}`}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights - Strategic Questions */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Leitura Executiva' : 'Executive Reading'}
          </h3>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-red-500/30">
                <TrendingDown className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <h4 className="font-semibold text-red-400 text-sm mb-1">{language === 'pt' ? 'VARIAÇÃO' : 'CHANGE'}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Resultado financeiro agravou ${Math.abs(totalYoYPct)}% YoY (${finData.total2025.toFixed(1)}M vs ${finData.total2024.toFixed(1)}M).`
                    : `Financial result worsened ${Math.abs(totalYoYPct)}% YoY (${finData.total2025.toFixed(1)}M vs ${finData.total2024.toFixed(1)}M).`}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/30">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-400 text-sm mb-1">{language === 'pt' ? 'DRIVER' : 'DRIVER'}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Câmbios desfavoráveis (-2.3M delta) e aumento de juros bancários (-0.4M) explicam a deterioração.`
                    : `Unfavorable FX (-2.3M delta) and increased bank interest (-0.4M) explain deterioration.`}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-500/30">
                <Info className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-400 text-sm mb-1">{language === 'pt' ? 'NATUREZA' : 'NATURE'}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `54% recorrente (juros) vs 46% volátil (câmbios, unwind). 11% não-caixa.`
                    : `54% recurring (interest) vs 46% volatile (FX, unwind). 11% non-cash.`}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/30">
                <Target className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h4 className="font-semibold text-emerald-400 text-sm mb-1">{language === 'pt' ? 'GESTÃO' : 'ACTION'}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Renegociar BCGA/BCS + aumentar hedge cambial natural.`
                    : `Renegotiate BCGA/BCS + increase natural FX hedge.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Executive KPIs - 5 Cards */}
      <div className="grid grid-cols-5 gap-4">
        <KPICard 
          title={language === 'pt' ? 'Resultado Financeiro Líquido' : 'Net Financial Result'} 
          value={finData.total2025} 
          unit="M USD" 
          vsBudgetPct={totalVsBudgetPct}
          yoyChange={totalYoYPct}
          inverse={true}
          tooltipData={{
            title: language === 'pt' ? 'Resultado Financeiro Líquido' : 'Net Financial Result',
            definition: language === 'pt' 
              ? 'Soma de todos os custos e proveitos financeiros: juros bancários, remuneração de tesouraria, câmbios e efeitos contabilísticos.'
              : 'Sum of all financial costs and income: bank interest, treasury remuneration, FX and accounting effects.',
            relevance: language === 'pt'
              ? 'Indicador agregado do burden financeiro e da qualidade da posição de financiamento.'
              : 'Aggregate indicator of financial burden and financing position quality.',
            currentReading: language === 'pt'
              ? 'Resultado de -5.9M USD agravou 84% YoY, com principal driver no resultado cambial adverso. Custos recorrentes representam 54% do total.'
              : 'Result of -5.9M USD worsened 84% YoY, with main driver in adverse FX result. Recurring costs represent 54% of total.'
          }}
        />
        <KPICard 
          title={language === 'pt' ? 'Juros Empréstimos Bancários' : 'Bank Loan Interest'} 
          value={finData.bankInterest.total2025} 
          unit="M USD" 
          vsBudgetPct={bankIntVsBudget}
          yoyChange={bankIntYoY}
          inverse={true}
          badge={{ label: language === 'pt' ? 'CAIXA' : 'CASH', bg: 'bg-red-100', text: 'text-red-700' }}
          tooltipData={{
            title: language === 'pt' ? 'Juros de Empréstimos Bancários' : 'Bank Loan Interest',
            definition: language === 'pt' 
              ? 'Encargos financeiros com dívida bancária de curto e médio prazo. Custo de serviço do passivo.'
              : 'Financial charges on short and medium-term bank debt. Liability servicing cost.',
            relevance: language === 'pt'
              ? 'Maior componente recorrente dos custos financeiros. Impacto directo em tesouraria.'
              : 'Largest recurring component of financial costs. Direct treasury impact.',
            currentReading: language === 'pt'
              ? 'Aumento de 14% YoY por entrada de nova linha BAI (0.9M) e maior utilização de CGA. Parcialmente compensado por renegociação BCGA/BCS (-0.8M delta).'
              : 'Increased 14% YoY from new BAI line (0.9M) and higher CGA utilization. Partially offset by BCGA/BCS renegotiation (-0.8M delta).'
          }}
        />
        <KPICard 
          title={language === 'pt' ? 'Unwind Prov. Ambiental' : 'Env. Provision Unwind'} 
          value={finData.unwindProvision.value2025} 
          unit="M USD" 
          vsBudgetPct={unwindVsBudget}
          yoyChange={unwindYoY}
          inverse={true}
          badge={{ label: language === 'pt' ? 'NÃO-CAIXA' : 'NON-CASH', bg: 'bg-blue-100', text: 'text-blue-700' }}
          tooltipData={{
            title: language === 'pt' ? 'Unwind da Provisão Ambiental' : 'Environmental Provision Unwind',
            definition: language === 'pt' 
              ? 'Custo financeiro contabilístico da actualização temporal da provisão para reabilitação ambiental.'
              : 'Accounting financial cost from time value adjustment of environmental rehabilitation provision.',
            relevance: language === 'pt'
              ? 'Impacto no resultado mas tipicamente sem saída de caixa imediata no período.'
              : 'P&L impact but typically no immediate cash outflow in the period.',
            currentReading: language === 'pt'
              ? 'Unwind de -0.6M USD representa actualização do saldo de provisão de 12.5M à taxa de desconto de 5%. Natureza contabilística, sem impacto imediato em FCF.'
              : 'Unwind of -0.6M USD represents update of 12.5M provision balance at 5% discount rate. Accounting nature, no immediate FCF impact.'
          }}
        />
        <KPICard 
          title={language === 'pt' ? 'Juros Aplicações Tesouraria' : 'Treasury Interest Income'} 
          value={finData.treasuryIncome.value2025} 
          unit="M USD" 
          vsBudgetPct={treasuryVsBudget}
          yoyChange={treasuryYoY}
          inverse={false}
          badge={{ label: language === 'pt' ? 'CAIXA' : 'CASH', bg: 'bg-emerald-100', text: 'text-emerald-700' }}
          tooltipData={{
            title: language === 'pt' ? 'Juros de Aplicações de Tesouraria' : 'Treasury Interest Income',
            definition: language === 'pt' 
              ? 'Rendimentos de depósitos a prazo e aplicações financeiras de curto prazo. Remuneração da posição de liquidez.'
              : 'Income from term deposits and short-term financial investments. Liquidity position remuneration.',
            relevance: language === 'pt'
              ? 'Mede a eficiência da tesouraria na monetização de excedentes de caixa.'
              : 'Measures treasury efficiency in monetizing cash surpluses.',
            currentReading: language === 'pt'
              ? 'Rendimento de 0.3M USD (+50% YoY) com yield médio de 1.1% sobre caixa média aplicada de 28M. Melhor gestão de excedentes.'
              : 'Income of 0.3M USD (+50% YoY) with 1.1% average yield on 28M average applied cash. Better surplus management.'
          }}
        />
        <KPICard 
          title={language === 'pt' ? 'Resultado Cambial Líquido' : 'Net FX Result'} 
          value={finData.fxResult.netValue2025} 
          unit="M USD" 
          vsBudgetPct={fxVsBudget}
          yoyChange={fxYoY !== -999 ? fxYoY : undefined}
          inverse={true}
          badge={{ label: language === 'pt' ? 'VOLÁTIL' : 'VOLATILE', bg: 'bg-amber-100', text: 'text-amber-700' }}
          tooltipData={{
            title: language === 'pt' ? 'Resultado Cambial Líquido' : 'Net FX Result',
            definition: language === 'pt' 
              ? 'Líquido de ganhos e perdas cambiais sobre posições em moeda estrangeira (dívida, caixa, fornecedores).'
              : 'Net of FX gains and losses on foreign currency positions (debt, cash, suppliers).',
            relevance: language === 'pt'
              ? 'Natureza volátil e dependente da exposição monetária líquida. Leitura separada do recorrente.'
              : 'Volatile nature dependent on net monetary exposure. Separate reading from recurring.',
            currentReading: language === 'pt'
              ? 'Resultado adverso de -1.8M vs +0.5M em 2024. Principal driver: desvalorização do AOA face ao USD impactando saldos de dívida. 65% realizado.'
              : 'Adverse result of -1.8M vs +0.5M in 2024. Main driver: AOA depreciation vs USD impacting debt balances. 65% realized.'
          }}
        />
      </div>

      {/* Executive Summary - Resumo Executivo Premium */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Resumo Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Análise consolidada do resultado financeiro' : 'Consolidated financial result analysis'}
        language={language}
        maxHeight="550px"
        icon={Info}
        dataTestId="financial-results-executive-summary"
        headerClassName="bg-[#0A192F] border-b border-slate-700"
      >
        <div className="space-y-6">
          {/* 1. Visão do Ano */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-slate-600"></div>
              {language === 'pt' ? 'Visão do Ano' : 'Year Overview'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, o <strong>resultado financeiro fechou em -USD 5,9M</strong>, deteriorando-se face aos <strong>-USD 3,2M de 2024</strong>. A variação do ano não foi explicada por um agravamento linear do custo da dívida, mas por uma combinação de três factores: <strong>maior pressão cambial</strong>, <strong>custo bancário recorrente ainda material</strong> e apenas <strong>compensação parcial da tesouraria</strong>. A leitura para a Gerência é que o resultado financeiro continua penalizado por componentes que têm natureza distinta e, por isso, não devem ser lidas da mesma forma dentro da DRE.</>
                  : <>In 2025, the <strong>financial result closed at -USD 5.9M</strong>, deteriorating from <strong>-USD 3.2M in 2024</strong>. The year's variation was not explained by a linear worsening of debt cost, but by a combination of three factors: <strong>greater FX pressure</strong>, <strong>still material recurring bank cost</strong> and only <strong>partial treasury compensation</strong>.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>Em termos de composição económica, o peso do impacto em caixa continuou dominante. Do total do resultado financeiro, cerca de <strong>USD 5,3M</strong> corresponderam a impacto efectivo em caixa, enquanto <strong>USD 0,6M</strong> tiveram natureza <strong>não-caixa / contabilística</strong>, associados ao <strong>unwind da provisão ambiental</strong>. Isto significa que a pressão financeira do ano não ficou apenas no plano contabilístico; teve efeito material sobre liquidez e geração de caixa.</>
                  : <>In terms of economic composition, the weight of cash impact remained dominant. Of the total financial result, about <strong>USD 5.3M</strong> corresponded to effective cash impact, while <strong>USD 0.6M</strong> had <strong>non-cash / accounting</strong> nature, associated with <strong>environmental provision unwind</strong>.</>}
              </p>
            </div>
          </div>

          {/* 2. Estrutura do Resultado Financeiro */}
          <div className="bg-amber-50/60 rounded-xl p-5 border border-amber-200">
            <h4 className="font-semibold text-sm text-amber-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-amber-600"></div>
              {language === 'pt' ? 'Estrutura do Resultado Financeiro' : 'Financial Result Structure'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>O principal bloco recorrente continua a ser o <strong>custo bancário</strong>, com <strong>juros de empréstimos de USD 3,2M</strong>, o que representa a componente estrutural mais relevante da rubrica. Em paralelo, o <strong>unwind da provisão ambiental</strong> adicionou <strong>USD 0,6M</strong> de custo contabilístico sem impacto em caixa. No lado positivo, os <strong>juros de aplicações de tesouraria</strong> contribuíram com <strong>USD 0,3M</strong>, mitigando parcialmente a pressão do funding.</>
                  : <>The main recurring block remains <strong>bank cost</strong>, with <strong>loan interest of USD 3.2M</strong>, representing the most relevant structural component. In parallel, <strong>environmental provision unwind</strong> added <strong>USD 0.6M</strong> of accounting cost without cash impact. On the positive side, <strong>treasury application interest</strong> contributed <strong>USD 0.3M</strong>.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A decomposição da rubrica permite separar três camadas de leitura:' : 'The breakdown allows separating three reading layers:'}
              </p>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="text-xs font-semibold text-red-700 mb-1.5">{language === 'pt' ? 'Custos Recorrentes' : 'Recurring Costs'}: <span className="text-red-800">-USD 3,8M</span></p>
                  <ul className="text-xs text-slate-600 space-y-0.5">
                    <li>• {language === 'pt' ? 'Juros bancários' : 'Bank interest'}: <span className="font-medium">-USD 3,2M</span></li>
                    <li>• {language === 'pt' ? 'Unwind prov. ambiental' : 'Env. prov. unwind'}: <span className="font-medium">-USD 0,6M</span></li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="text-xs font-semibold text-amber-700 mb-1.5">{language === 'pt' ? 'Volátil / Não Operacional' : 'Volatile / Non-Op.'}: <span className="text-amber-800">-USD 2,1M</span></p>
                  <ul className="text-xs text-slate-600 space-y-0.5">
                    <li>• {language === 'pt' ? 'Dif. cambiais líquidas' : 'Net FX differences'}: <span className="font-medium">-USD 1,8M</span></li>
                    <li>• {language === 'pt' ? 'Outros financeiros' : 'Other financial'}: <span className="font-medium">-USD 0,3M</span></li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="text-xs font-semibold text-emerald-700 mb-1.5">{language === 'pt' ? 'Proveitos Financeiros' : 'Financial Income'}: <span className="text-emerald-800">+USD 0,3M</span></p>
                  <ul className="text-xs text-slate-600 space-y-0.5">
                    <li>• {language === 'pt' ? 'Juros de tesouraria' : 'Treasury interest'}: <span className="font-medium">+USD 0,3M</span></li>
                  </ul>
                </div>
              </div>
              <p className="text-slate-600 italic text-xs mt-2">
                {language === 'pt' 
                  ? 'A principal implicação é que o resultado financeiro de 2025 não deve ser lido como uma simples função da dívida. A rubrica combina um núcleo recorrente previsível, um bloco cambial volátil e uma almofada de tesouraria ainda pequena face ao custo global.'
                  : 'The main implication is that the 2025 financial result should not be read as a simple function of debt. The item combines a predictable recurring core, a volatile FX block and a treasury cushion still small relative to global cost.'}
              </p>
            </div>
          </div>

          {/* 3. Drivers do Agravamento Face a 2024 */}
          <div className="bg-rose-50/60 rounded-xl p-5 border border-rose-200">
            <h4 className="font-semibold text-sm text-rose-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-rose-600"></div>
              {language === 'pt' ? 'Drivers do Agravamento Face a 2024' : 'Deterioration Drivers vs 2024'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A deterioração de <strong>-USD 3,2M para -USD 5,9M</strong> foi explicada sobretudo pelo <strong>resultado cambial</strong>, que se tornou o principal factor negativo do ano. O efeito veio maioritariamente da exposição <strong>USD/AOA</strong>, associada à dívida, e foi complementado por impactos em <strong>EUR/USD</strong> e <strong>ZAR/USD</strong>. A secção confirma, por isso, que parte relevante do agravamento não resulta de pior disciplina financeira, mas da exposição do balanço a factores cambiais adversos.</>
                  : <>The deterioration from <strong>-USD 3.2M to -USD 5.9M</strong> was explained mainly by <strong>FX result</strong>, which became the main negative factor of the year. The effect came mainly from <strong>USD/AOA</strong> exposure, associated with debt, complemented by impacts in <strong>EUR/USD</strong> and <strong>ZAR/USD</strong>.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>Do lado do custo bancário, o ano mostrou um comportamento misto entre instituições. Houve aumento do custo em alguns instrumentos, incluindo a nova linha <strong>BAI</strong>, enquanto a renegociação / melhoria noutros financiadores mitigou parcialmente essa subida. Em termos de leitura consolidada, o <strong>WACD de 8,5%</strong> continua a indicar um custo de funding elevado para a estrutura actual, ainda que mais estável do que poderia ser num contexto de maior pressão cambial.</>
                  : <>On the bank cost side, the year showed mixed behavior among institutions. There was cost increase in some instruments, including the new <strong>BAI</strong> line, while renegotiation with other financiers partially mitigated this increase. The <strong>WACD of 8.5%</strong> continues to indicate a high funding cost.</>}
              </p>
            </div>
          </div>

          {/* 4. Juros e Custo do Funding */}
          <div className="bg-purple-50/60 rounded-xl p-5 border border-purple-200">
            <h4 className="font-semibold text-sm text-purple-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-purple-600"></div>
              {language === 'pt' ? 'Juros e Custo do Funding' : 'Interest & Funding Cost'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, os <strong>juros de empréstimos bancários totalizaram USD 3,2M</strong>, mantendo-se como a componente recorrente mais relevante do resultado financeiro. A leitura desta rubrica não deve ser feita apenas pelo valor absoluto, mas pela sua composição entre <strong>volume de dívida</strong>, <strong>mix de instrumentos</strong> e <strong>custo médio contratado</strong>. O ano mostrou um comportamento misto entre financiadores: houve maior pressão em algumas linhas, incluindo a entrada da nova linha BAI, enquanto a renegociação e melhoria de condições em outros instrumentos mitigou parcialmente essa subida.</>
                  : <>In 2025, <strong>bank loan interest totaled USD 3.2M</strong>, remaining the most relevant recurring component of the financial result. This item should not be read only by absolute value, but by its composition between <strong>debt volume</strong>, <strong>instrument mix</strong> and <strong>contracted average cost</strong>.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A análise por banco confirma esta leitura. O aumento do custo esteve sobretudo associado a:' : 'Bank-by-bank analysis confirms this reading. Cost increase was mainly associated with:'}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-2">
                <li>• {language === 'pt' ? 'BAI, como nova fonte de financiamento com impacto incremental no custo anual;' : 'BAI, as new financing source with incremental impact on annual cost;'}</li>
                <li>• {language === 'pt' ? 'maior utilização relativa de linhas com custo mais elevado;' : 'higher relative use of higher-cost lines;'}</li>
                <li>• {language === 'pt' ? 'apenas compensação parcial do aumento através de condições mais favoráveis em BCGA / BCS.' : 'only partial offset through more favorable conditions at BCGA / BCS.'}</li>
              </ul>
              <p>
                {language === 'pt' 
                  ? <>Em termos consolidados, o <strong>WACD de 8,5%</strong> continua a indicar um custo de funding material para a estrutura actual. Para a Gerência, o ponto central não é apenas que os juros bancários continuam elevados, mas que esta rubrica representa o <strong>núcleo recorrente e previsível</strong> do resultado financeiro, sendo por isso a principal alavanca de actuação na componente não operacional do resultado.</>
                  : <>In consolidated terms, the <strong>WACD of 8.5%</strong> continues to indicate a material funding cost for the current structure. For Management, the central point is not just that bank interest remains high, but that this item represents the <strong>recurring and predictable core</strong> of the financial result.</>}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-purple-100 mt-2">
                <p className="text-xs text-purple-700 font-medium">
                  <span className="text-purple-500">▸ </span>
                  {language === 'pt' 
                    ? 'A melhoria futura desta linha dependerá sobretudo de três factores: desalavancagem, reperfilamento do mix bancário e renegociação adicional de spreads.'
                    : 'Future improvement of this line will depend mainly on three factors: deleveraging, bank mix rebalancing and additional spread renegotiation.'}
                </p>
              </div>
            </div>
          </div>

          {/* 5. Unwind da Provisão Ambiental */}
          <div className="bg-teal-50/60 rounded-xl p-5 border border-teal-200">
            <h4 className="font-semibold text-sm text-teal-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-teal-600"></div>
              {language === 'pt' ? 'Unwind da Provisão Ambiental' : 'Environmental Provision Unwind'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>O unwind da provisão ambiental representou em 2025 um custo de <strong>USD 0,6M</strong>, mantendo natureza <strong>financeira contabilística e não-caixa</strong>. Esta rubrica não corresponde a novo risco ambiental gerado no período, nem a uma saída de caixa adicional; traduz antes a <strong>actualização temporal da obrigação</strong> já reconhecida em balanço, à medida que a provisão se aproxima do seu horizonte de realização.</>
                  : <>The environmental provision unwind represented in 2025 a cost of <strong>USD 0.6M</strong>, maintaining <strong>accounting and non-cash financial</strong> nature. This item does not correspond to new environmental risk generated in the period, nor to additional cash outflow; it rather translates the <strong>time value update of the obligation</strong> already recognized on the balance sheet.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A distinção face aos juros bancários é importante. Enquanto os juros de dívida representam um custo efectivo de funding com impacto directo sobre liquidez, o unwind deve ser lido como:' : 'The distinction from bank interest is important. While debt interest represents an effective funding cost with direct liquidity impact, the unwind should be read as:'}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-2">
                <li>• {language === 'pt' ? 'custo financeiro contabilístico;' : 'accounting financial cost;'}</li>
                <li>• {language === 'pt' ? 'sem impacto em free cash flow do período;' : 'no impact on period free cash flow;'}</li>
                <li>• {language === 'pt' ? 'associado ao saldo da provisão existente e à taxa de desconto aplicada.' : 'associated with existing provision balance and applied discount rate.'}</li>
              </ul>
              <p>
                {language === 'pt' 
                  ? <>Para a Gerência, esta separação é relevante porque evita duas leituras incorrectas: a primeira seria tratar o unwind como custo bancário; a segunda seria interpretá-lo como agravamento operacional da obrigação ambiental do ano. Na realidade, o unwind reflecte apenas a <strong>evolução financeira da obrigação já reconhecida</strong>.</>
                  : <>For Management, this separation is relevant because it avoids two incorrect readings: the first would be treating the unwind as bank cost; the second would be interpreting it as operational worsening of the year's environmental obligation. In reality, the unwind reflects only the <strong>financial evolution of the already recognized obligation</strong>.</>}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-teal-100 mt-2">
                <p className="text-xs text-teal-700 font-medium">
                  <span className="text-teal-500">▸ </span>
                  {language === 'pt' 
                    ? 'Em termos executivos, isto significa que o resultado financeiro de 2025 inclui uma componente negativa que penaliza o P&L, mas não representa pressão adicional sobre caixa, devendo por isso ser analisada separadamente da componente recorrente de funding.'
                    : 'In executive terms, this means the 2025 financial result includes a negative component that penalizes P&L, but does not represent additional cash pressure, and should therefore be analyzed separately from the recurring funding component.'}
                </p>
              </div>
            </div>
          </div>

          {/* 6. Caixa vs Contabilístico */}
          <div className="bg-blue-50/60 rounded-xl p-5 border border-blue-200">
            <h4 className="font-semibold text-sm text-blue-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              {language === 'pt' ? 'Caixa vs Contabilístico' : 'Cash vs Accounting'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A secção mostra também uma distinção importante para o CFO: o resultado financeiro de 2025 foi maioritariamente um tema de <strong>caixa</strong>, não apenas de P&L. O impacto líquido em caixa foi de <strong>-USD 5,3M</strong>, composto por:</>
                  : <>This section also shows an important distinction for the CFO: the 2025 financial result was mostly a <strong>cash</strong> matter, not just P&L. Net cash impact was <strong>-USD 5.3M</strong>, composed of:</>}
              </p>
              <div className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-600">{language === 'pt' ? 'Juros bancários' : 'Bank interest'}:</span>
                    <span className="font-semibold text-red-600">-USD 3,2M</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{language === 'pt' ? 'Juros tesouraria' : 'Treasury interest'}:</span>
                    <span className="font-semibold text-emerald-600">+USD 0,3M</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{language === 'pt' ? 'Câmbios realizados' : 'Realized FX'}:</span>
                    <span className="font-semibold text-red-600">-USD 1,2M</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{language === 'pt' ? 'Câmbios favoráveis' : 'Favorable FX'}:</span>
                    <span className="font-semibold text-emerald-600">+USD 0,6M</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{language === 'pt' ? 'Outros custos' : 'Other costs'}:</span>
                    <span className="font-semibold text-red-600">-USD 0,6M</span>
                  </div>
                </div>
              </div>
              <p className="text-slate-600 italic text-xs">
                {language === 'pt' 
                  ? <>A componente puramente contabilística ficou limitada ao <strong>unwind da provisão ambiental (-USD 0,6M)</strong>, o que significa que quase toda a pressão da rubrica teve tradução económica efectiva no período.</>
                  : <>The purely accounting component was limited to <strong>environmental provision unwind (-USD 0.6M)</strong>, meaning almost all item pressure had effective economic translation in the period.</>}
              </p>
            </div>
          </div>

          {/* 7. Síntese à Gerência - Premium Gold Callout */}
          <div className="bg-gradient-to-r from-[#FBF8F0] to-[#F8F4E8] rounded-xl p-5 border border-[#B89A3C]/30">
            <h4 className="font-semibold text-sm text-[#8B7A2F] mb-3 flex items-center gap-2 uppercase tracking-wider">
              <Target className="w-4 h-4" />
              {language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, o <strong>resultado financeiro agravou-se para -USD 5,9M</strong>, pressionado sobretudo por <strong>câmbio</strong> e por um <strong>custo bancário recorrente ainda material</strong>. A tesouraria gerou <strong>USD 0,3M</strong> de proveitos, mas esse ganho foi insuficiente para compensar a pressão combinada dos juros bancários, do unwind contabilístico e das diferenças cambiais líquidas.</>
                  : <>In 2025, the <strong>financial result worsened to -USD 5.9M</strong>, pressured mainly by <strong>FX</strong> and <strong>still material recurring bank cost</strong>. Treasury generated <strong>USD 0.3M</strong> in income, but this gain was insufficient to offset the combined pressure.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A principal leitura para a Gerência é que o resultado financeiro permanece dividido entre:' : 'The main reading for Management is that the financial result remains divided between:'}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-2">
                <li>• {language === 'pt' ? 'um núcleo recorrente de custo associado ao funding;' : 'a recurring cost core associated with funding;'}</li>
                <li>• {language === 'pt' ? 'uma componente volátil associada a câmbios;' : 'a volatile component associated with FX;'}</li>
                <li>• {language === 'pt' ? 'e uma almofada de tesouraria ainda pequena face ao custo global.' : 'and a treasury cushion still small relative to global cost.'}</li>
              </ul>
              <div className="bg-white/60 rounded-lg p-3 border border-[#B89A3C]/20 mt-3">
                <p className="text-xs text-[#6B5A1F] font-medium">
                  <span className="text-[#B89A3C]">▸ </span>
                  {language === 'pt' 
                    ? 'O foco de gestão para as páginas seguintes deve estar em três frentes: custo do funding, rentabilização da liquidez e redução da exposição cambial líquida, porque é essa combinação que determinará a capacidade de estabilizar esta rubrica nos próximos períodos.'
                    : 'Management focus for the following pages should be on three fronts: funding cost, liquidity monetization and net FX exposure reduction, as this combination will determine the ability to stabilize this item in coming periods.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* BLOCK 1: Bridge do Resultado Financeiro */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-lg font-bold text-primary">
              {language === 'pt' ? 'Bridge do Resultado Financeiro' : 'Financial Result Bridge'}
            </h3>
            <p className="text-slate-400 text-xs mt-0.5">
              {language === 'pt' ? `Decomposição da variação 2024 → 2025 (M USD)` : `2024 → 2025 Variance Decomposition (M USD)`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-emerald-500"></div>
              <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Favorável' : 'Favorable'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-red-500"></div>
              <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Desfavorável' : 'Unfavorable'}</span>
            </div>
          </div>
        </div>
        <BridgeChart data={bridgeData} language={language} />
        <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
          <p className="text-xs text-slate-600 leading-relaxed">
            <span className="font-semibold text-primary">Leitura: </span>
            {language === 'pt' 
              ? `O resultado financeiro deteriorou de -3.2M para -5.9M. O principal driver negativo foi o resultado cambial (-2.3M delta), seguido dos juros bancários (-0.4M). A tesouraria contribuiu favoravelmente (+0.1M) mas não compensou a pressão.`
              : `Financial result deteriorated from -3.2M to -5.9M. Main negative driver was FX result (-2.3M delta), followed by bank interest (-0.4M). Treasury contributed favorably (+0.1M) but didn't offset the pressure.`}
          </p>
        </div>
      </div>

      {/* BLOCK 2: Cash vs Non-Cash + Recurring vs Volatile */}
      <div className="grid grid-cols-2 gap-4">
        {/* Cash vs Non-Cash */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-serif text-base font-bold text-primary mb-4">
            {language === 'pt' ? 'Impacto de Caixa vs Contabilístico' : 'Cash vs Accounting Impact'}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-red-50/50 rounded-lg border border-red-100">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-800">{language === 'pt' ? 'Impacto Caixa' : 'Cash Impact'}</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-red-700">-5.3M USD</span>
                <span className="text-xs text-red-500 ml-2">89%</span>
              </div>
            </div>
            <div className="pl-4 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Juros bancários' : 'Bank interest'}</span>
                <span className="font-medium text-red-600">-3.2M</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Câmbios (realizado)' : 'FX (realized)'}</span>
                <span className="font-medium text-red-600">-1.2M</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Outros custos' : 'Other costs'}</span>
                <span className="font-medium text-red-600">-0.6M</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Juros tesouraria' : 'Treasury income'}</span>
                <span className="font-medium text-emerald-600">+0.3M</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Câmbios favoráveis' : 'Favorable FX'}</span>
                <span className="font-medium text-emerald-600">+0.6M</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">{language === 'pt' ? 'Não-Caixa / Contabilístico' : 'Non-Cash / Accounting'}</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-blue-700">-0.6M USD</span>
                <span className="text-xs text-blue-500 ml-2">11%</span>
              </div>
            </div>
            <div className="pl-4 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Unwind provisão ambiental' : 'Environmental unwind'}</span>
                <span className="font-medium text-blue-600">-0.6M</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recurring vs Volatile */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-serif text-base font-bold text-primary mb-4">
            {language === 'pt' ? 'Recorrente vs Volátil' : 'Recurring vs Volatile'}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-800">{language === 'pt' ? 'Custos Recorrentes' : 'Recurring Costs'}</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-slate-700">-3.8M USD</span>
                <span className="text-xs text-slate-500 ml-2">64%</span>
              </div>
            </div>
            <div className="pl-4 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Juros bancários' : 'Bank interest'}</span>
                <span className="font-medium text-slate-700">-3.2M</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Unwind provisão' : 'Provision unwind'}</span>
                <span className="font-medium text-slate-700">-0.6M</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50/50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">{language === 'pt' ? 'Volátil / Não Operacional' : 'Volatile / Non-Operational'}</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-amber-700">-2.1M USD</span>
                <span className="text-xs text-amber-500 ml-2">36%</span>
              </div>
            </div>
            <div className="pl-4 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Diferenças cambiais líquidas' : 'Net FX differences'}</span>
                <span className="font-medium text-amber-700">-1.8M</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Outros financeiros' : 'Other financial'}</span>
                <span className="font-medium text-amber-700">-0.3M</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">{language === 'pt' ? 'Proveitos Financeiros' : 'Financial Income'}</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-emerald-700">+0.3M USD</span>
              </div>
            </div>
            <div className="pl-4 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">{language === 'pt' ? 'Juros de tesouraria' : 'Treasury income'}</span>
                <span className="font-medium text-emerald-600">+0.3M</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === CUSTO FINANCEIRO (UNWIND) - PROVISÃO AMBIENTAL === */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-[#0A192F] px-5 py-3">
          <h3 className="font-serif text-base font-bold text-white flex items-center gap-2">
            <Leaf className="w-5 h-5 text-emerald-400" />
            {language === 'pt' ? 'Custo Financeiro (Unwind) - Provisão Ambiental' : 'Financial Cost (Unwind) - Environmental Provision'}
          </h3>
          <p className="text-slate-300 text-xs mt-0.5">
            {language === 'pt' ? 'Actualização temporal do passivo de reabilitação ambiental | Componente não-caixa' : 'Time value update of environmental rehabilitation liability | Non-cash component'}
          </p>
        </div>
        
        <div className="p-5">
          {/* KPIs - 4 Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {/* KPI 1: Custo Unwind */}
            <div className="bg-white rounded-xl border border-blue-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-blue-100">
                <RefreshCw className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Custo Unwind 2025' : 'Unwind Cost 2025'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                -{finData.unwindProvision.custoUnwind.v2025}<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  finData.unwindProvision.custoUnwind.v2025 > finData.unwindProvision.custoUnwind.budget2025 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {finData.unwindProvision.custoUnwind.v2025 > finData.unwindProvision.custoUnwind.budget2025 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {((finData.unwindProvision.custoUnwind.v2025 - finData.unwindProvision.custoUnwind.budget2025) / finData.unwindProvision.custoUnwind.budget2025 * 100).toFixed(0)}% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit ${
                  finData.unwindProvision.custoUnwind.v2025 > finData.unwindProvision.custoUnwind.v2024 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {finData.unwindProvision.custoUnwind.v2025 > finData.unwindProvision.custoUnwind.v2024 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {((finData.unwindProvision.custoUnwind.v2025 - finData.unwindProvision.custoUnwind.v2024) / finData.unwindProvision.custoUnwind.v2024 * 100).toFixed(0)}% YoY
                </span>
              </div>
            </div>

            {/* KPI 2: Taxa de Actualização */}
            <div className="bg-white rounded-xl border border-amber-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-amber-100">
                <Percent className="w-4 h-4 text-amber-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Taxa de Actualização' : 'Discount Rate'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                {finData.unwindProvision.taxaAtualizacao.v2025}<span className="text-base text-slate-400 ml-1 font-sans">%</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-rose-100 text-rose-700">
                  <TrendingUp className="w-3 h-3" />
                  +{(finData.unwindProvision.taxaAtualizacao.v2025 - finData.unwindProvision.taxaAtualizacao.v2024).toFixed(1)}p.p. YoY
                </span>
              </div>
            </div>

            {/* KPI 3: Saldo da Provisão */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-slate-100">
                <Wallet className="w-4 h-4 text-slate-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Saldo da Provisão' : 'Provision Balance'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                {finData.unwindProvision.saldoProvisao.v2025}<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-amber-100 text-amber-700">
                  <TrendingUp className="w-3 h-3" />
                  +{((finData.unwindProvision.saldoProvisao.v2025 - finData.unwindProvision.saldoProvisao.v2024) / finData.unwindProvision.saldoProvisao.v2024 * 100).toFixed(0)}% YoY
                </span>
              </div>
            </div>

            {/* KPI 4: Anos Restantes */}
            <div className="bg-white rounded-xl border border-emerald-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-emerald-100">
                <Clock className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Horizonte de Reabilitação' : 'Rehabilitation Horizon'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                {finData.unwindProvision.anosRestantes.v2025}<span className="text-base text-slate-400 ml-1 font-sans">{language === 'pt' ? 'anos' : 'years'}</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-amber-100 text-amber-700">
                  <TrendingUp className="w-3 h-3" />
                  +{finData.unwindProvision.anosRestantes.v2025 - finData.unwindProvision.anosRestantes.v2024} vs 2024
                </span>
              </div>
            </div>
          </div>

          {/* Bridge Chart - Drivers da Variação do Custo */}
          <div className="bg-slate-50/50 rounded-xl border border-slate-200 p-4 mb-4">
            <h4 className="font-semibold text-sm text-slate-700 mb-3">
              {language === 'pt' ? 'Drivers da Variação do Custo de Unwind (2024 → 2025)' : 'Unwind Cost Variation Drivers (2024 → 2025)'}
            </h4>
            <BridgeChart 
              data={[
                { name: '2024', value: finData.unwindProvision.custoUnwind.v2024, type: 'start' },
                { name: language === 'pt' ? 'Combustível' : 'Fuel', value: finData.unwindProvision.driversVariacao.custoCombustivel.impacto / 100 * finData.unwindProvision.custoUnwind.v2024, type: 'change' },
                { name: language === 'pt' ? 'Taxa Desc.' : 'Disc. Rate', value: finData.unwindProvision.driversVariacao.taxaAtualizacao.impacto / 100 * finData.unwindProvision.custoUnwind.v2024, type: 'change' },
                { name: language === 'pt' ? 'Prazo Reab.' : 'Rehab. Term', value: finData.unwindProvision.driversVariacao.anosReabilitacao.impacto / 100 * finData.unwindProvision.custoUnwind.v2024, type: 'change' },
                { name: language === 'pt' ? 'Rev. Técnica' : 'Tech. Review', value: finData.unwindProvision.driversVariacao.revisaoTecnica.impacto / 100 * finData.unwindProvision.custoUnwind.v2024, type: 'change' },
                { name: '2025', value: finData.unwindProvision.custoUnwind.v2025, type: 'end' },
              ]} 
              language={language} 
            />
            <div className="flex justify-center gap-6 mt-2 text-[10px]">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-emerald-500"></div>
                <span className="text-slate-500">{language === 'pt' ? 'Redução' : 'Reduction'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-red-500"></div>
                <span className="text-slate-500">{language === 'pt' ? 'Aumento' : 'Increase'}</span>
              </div>
            </div>
          </div>

          {/* Drivers de Variação - Bullets */}
          <div className="bg-blue-50/50 rounded-xl border border-blue-200 p-4 mb-4">
            <h4 className="font-semibold text-sm text-blue-800 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {language === 'pt' ? 'Explicação dos Drivers de Variação' : 'Variation Drivers Explanation'}
            </h4>
            <div className="space-y-2">
              {Object.entries(finData.unwindProvision.driversVariacao).map(([key, driver], idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    driver.impacto > 0 ? 'bg-rose-100' : 'bg-emerald-100'
                  }`}>
                    {driver.impacto > 0 ? (
                      <TrendingUp className="w-3 h-3 text-rose-600" />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-emerald-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-700 leading-relaxed">
                      <span className={`font-semibold ${driver.impacto > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {driver.impacto > 0 ? '+' : ''}{driver.impacto}%
                      </span>
                      <span className="mx-1.5">—</span>
                      {driver.descricao}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Leitura Executiva */}
          <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50/30 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-700">{language === 'pt' ? 'Leitura para o Board: ' : 'Board Reading: '}</span>
              {language === 'pt' 
                ? <>O <strong>custo de unwind de USD 0,6M</strong> representa a actualização temporal da <strong>provisão ambiental de USD 11,1M</strong> à taxa de desconto de <strong>5,0%</strong>. Este custo é <strong>não-caixa</strong>, sem impacto imediato em tesouraria. O aumento de <strong>+20% YoY</strong> resulta da extensão do horizonte de reabilitação (10 → 12 anos) e da subida da taxa de desconto. A revisão técnica do plano de encerramento da mina permitiu reduzir parcialmente o impacto (-12%), mas o efeito líquido foi ainda assim de aumento. O saldo da provisão cresceu <strong>31% YoY</strong> para cobrir custos de longo prazo.</>
                : <>The <strong>unwind cost of USD 0.6M</strong> represents the time value update of the <strong>environmental provision of USD 11.1M</strong> at a <strong>5.0%</strong> discount rate. This cost is <strong>non-cash</strong>, with no immediate treasury impact. The <strong>+20% YoY</strong> increase results from rehabilitation horizon extension (10 → 12 years) and discount rate increase. Technical review of the mine closure plan partially reduced the impact (-12%), but the net effect was still an increase. The provision balance grew <strong>31% YoY</strong> to cover long-term costs.</>}
            </p>
          </div>
        </div>
      </div>

      {/* BLOCK 3: Detailed Analysis by Nature - 4 Sections */}
      <div className="grid grid-cols-2 gap-4">
        
        {/* Section 1: Bank Loan Interest */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h3 className="font-serif text-base font-bold text-primary">
                {language === 'pt' ? 'Juros de Empréstimos Bancários' : 'Bank Loan Interest'}
              </h3>
              <p className="text-slate-400 text-[10px]">
                {language === 'pt' ? 'Custo de financiamento do passivo' : 'Liability financing cost'}
              </p>
            </div>
          </div>
          
          <div className="space-y-2.5">
            {finData.bankInterest.byBank.map((item, idx) => {
              const maxVal = 1.6;
              const width2025 = (item.v2025 / maxVal * 100);
              const width2024 = (item.v2024 / maxVal * 100);
              const delta = item.v2025 - item.v2024;
              return (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-medium text-slate-700">{item.bank}</span>
                      {item.isNew && (
                        <span className="text-[9px] bg-amber-50 text-amber-700 px-1 py-0.5 rounded font-medium">
                          {language === 'pt' ? 'NOVO' : 'NEW'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">{item.rate}%</span>
                      <span className={`text-[10px] font-semibold ${delta > 0 ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(1)}M
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="flex-1 bg-slate-50 rounded-sm h-2.5 relative overflow-hidden">
                      <div className="absolute left-0 top-0 h-full bg-red-500 rounded-sm" style={{ width: `${width2025}%` }} />
                    </div>
                    <span className="text-[10px] font-semibold text-red-600 w-6 text-right">{item.v2025.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="flex-1 bg-slate-50 rounded-sm h-2.5 relative overflow-hidden">
                      <div className="absolute left-0 top-0 h-full bg-slate-300 rounded-sm" style={{ width: `${width2024}%` }} />
                    </div>
                    <span className="text-[10px] text-slate-400 w-6 text-right">{item.v2024 === 0 ? '—' : item.v2024.toFixed(1)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-slate-700">{language === 'pt' ? 'WACD (Custo médio)' : 'WACD (Avg. cost)'}</span>
              <div className="flex items-center gap-2">
                <span className="font-bold text-primary">{finData.bankInterest.wacd}%</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  finData.bankInterest.wacd > finData.bankInterest.wacdBudget ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {(finData.bankInterest.wacd - finData.bankInterest.wacdBudget) >= 0 ? '+' : ''}{(finData.bankInterest.wacd - finData.bankInterest.wacdBudget).toFixed(1)}p.p. vs Orç.
                </span>
              </div>
            </div>
          </div>
          
          <div className="mt-3 p-2.5 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-600 leading-relaxed">
              <span className="font-semibold">{language === 'pt' ? 'Leitura: ' : 'Reading: '}</span>
              {language === 'pt' 
                ? 'Aumento dos juros por nova linha BAI e maior utilização CGA. Renegociação BCGA/BCS reduziu spread e mitigou parcialmente o aumento.'
                : 'Interest increase from new BAI line and higher CGA utilization. BCGA/BCS renegotiation reduced spread and partially mitigated the increase.'}
            </p>
          </div>
        </div>

        {/* Section 2: Resumo dos Empréstimos Bancários (Compact) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#0A192F]/10 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-[#0A192F]" />
            </div>
            <div>
              <h3 className="font-serif text-base font-bold text-primary">
                {language === 'pt' ? 'Resumo dos Empréstimos Bancários' : 'Banking Loans Summary'}
              </h3>
              <p className="text-slate-400 text-[10px]">
                {language === 'pt' ? 'Estrutura de dívida | Posição 31.12.2025' : 'Debt structure | Position 31.12.2025'}
              </p>
            </div>
          </div>
          
          {/* Mini KPIs */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-200">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Dívida Total' : 'Total Debt'}</p>
              <p className="text-lg font-bold text-primary">45.2M <span className="text-xs font-normal text-slate-400">USD</span></p>
            </div>
            <div className="bg-amber-50/50 rounded-lg p-2.5 border border-amber-100">
              <p className="text-[10px] text-amber-600 uppercase tracking-wider mb-0.5">{language === 'pt' ? 'Taxa Média' : 'Avg. Rate'}</p>
              <p className="text-lg font-bold text-amber-700">8.2%</p>
            </div>
          </div>
          
          {/* Lista compacta de empréstimos */}
          <div className="space-y-1.5 mb-3">
            <div className="flex justify-between items-center text-[10px] text-slate-400 uppercase tracking-wider pb-1 border-b border-slate-100">
              <span>{language === 'pt' ? 'Banco' : 'Bank'}</span>
              <span>{language === 'pt' ? 'Saldo' : 'Balance'}</span>
            </div>
            <div className="flex justify-between items-center text-xs bg-amber-50/30 px-1.5 py-1 rounded">
              <span className="text-slate-700 font-medium">BFA - MLP</span>
              <span className="font-semibold text-slate-800">18.5M <span className="text-[10px] text-amber-600">41%</span></span>
            </div>
            <div className="flex justify-between items-center text-xs px-1.5 py-1">
              <span className="text-slate-600">BIC - W. Cap.</span>
              <span className="font-medium text-slate-700">9.2M <span className="text-[10px] text-slate-400">20%</span></span>
            </div>
            <div className="flex justify-between items-center text-xs px-1.5 py-1">
              <span className="text-slate-600">Std Bank - PF</span>
              <span className="font-medium text-slate-700">7.4M <span className="text-[10px] text-slate-400">16%</span></span>
            </div>
            <div className="flex justify-between items-center text-xs bg-rose-50/30 px-1.5 py-1 rounded">
              <span className="text-slate-700">BAI - Capital</span>
              <span className="font-medium text-slate-700">5.8M <span className="text-[10px] text-rose-600">Mar'26</span></span>
            </div>
            <div className="flex justify-between items-center text-xs px-1.5 py-1">
              <span className="text-slate-600">BPC - C/C</span>
              <span className="font-medium text-slate-700">4.3M <span className="text-[10px] text-slate-400">10%</span></span>
            </div>
          </div>
          
          <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
            <p className="text-[10px] text-slate-600 leading-relaxed">
              <span className="font-semibold">{language === 'pt' ? 'Leitura: ' : 'Reading: '}</span>
              {language === 'pt' 
                ? 'BFA concentra 41% da dívida. Próxima maturidade crítica: BAI em Mar/2026. Taxa média 8.2% (+0.6p.p. YoY).'
                : 'BFA concentrates 41% of debt. Next critical maturity: BAI in Mar/2026. Avg. rate 8.2% (+0.6p.p. YoY).'}
            </p>
          </div>
        </div>

        {/* Section 3: Treasury Income */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <PiggyBank className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-serif text-base font-bold text-primary">
                {language === 'pt' ? 'Juros de Aplicações de Tesouraria' : 'Treasury Application Interest'}
              </h3>
              <p className="text-slate-400 text-[10px]">
                {language === 'pt' ? 'Rendimento da posição de liquidez' : 'Liquidity position income'}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] text-emerald-600 uppercase tracking-wider mb-1">{language === 'pt' ? 'Juros 2025' : 'Interest 2025'}</p>
              <p className="text-lg font-bold text-emerald-700">+{finData.treasuryIncome.value2025.toFixed(1)}M</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Caixa Média' : 'Avg Cash'}</p>
              <p className="text-lg font-bold text-slate-700">{finData.treasuryIncome.avgCashApplied}M</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Yield Médio' : 'Avg Yield'}</p>
              <p className="text-lg font-bold text-slate-700">{finData.treasuryIncome.avgYield}%</p>
            </div>
          </div>
          
          <div className="space-y-2 mb-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Por instrumento' : 'By instrument'}</p>
            {finData.treasuryIncome.byInstrument.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center text-xs">
                <span className="text-slate-600">{item.name}</span>
                <span className="font-semibold text-emerald-600">+{item.value.toFixed(2)}M</span>
              </div>
            ))}
          </div>
          
          <div className="mt-3 p-2.5 bg-emerald-50/50 rounded-lg border border-emerald-100">
            <p className="text-[10px] text-emerald-700 leading-relaxed">
              <span className="font-semibold">{language === 'pt' ? 'Leitura: ' : 'Reading: '}</span>
              {language === 'pt' 
                ? 'Melhoria nos juros de tesouraria (+50% YoY) por melhor gestão de excedentes e condições de mercado mais favoráveis. Monetização da liquidez compensou parcialmente a pressão financeira.'
                : 'Improvement in treasury income (+50% YoY) from better surplus management and more favorable market conditions. Liquidity monetization partially offset financial pressure.'}
            </p>
          </div>
        </div>

        {/* Section 4: FX Result */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Globe className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-serif text-base font-bold text-primary">
                {language === 'pt' ? 'Resultado Cambial Líquido' : 'Net FX Result'}
              </h3>
              <p className="text-slate-400 text-[10px]">
                {language === 'pt' ? 'Efeito de exposição cambial | Volátil' : 'Currency exposure effect | Volatile'}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-red-50/50 rounded-lg p-3 border border-red-100">
              <p className="text-[10px] text-red-600 uppercase tracking-wider mb-1">{language === 'pt' ? 'Resultado Líq.' : 'Net Result'}</p>
              <p className="text-lg font-bold text-red-700">{finData.fxResult.netValue2025.toFixed(1)}M</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
              <p className="text-[10px] text-emerald-600 uppercase tracking-wider mb-1">{language === 'pt' ? 'Favorável' : 'Favorable'}</p>
              <p className="text-lg font-bold text-emerald-700">+{finData.fxResult.favorable2025.toFixed(1)}M</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
              <p className="text-[10px] text-red-600 uppercase tracking-wider mb-1">{language === 'pt' ? 'Desfavorável' : 'Unfavorable'}</p>
              <p className="text-lg font-bold text-red-700">{finData.fxResult.unfavorable2025.toFixed(1)}M</p>
            </div>
          </div>
          
          <div className="space-y-2 mb-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{language === 'pt' ? 'Por moeda / exposição' : 'By currency / exposure'}</p>
            {finData.fxResult.byCurrency.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">{item.currency}</span>
                  <span className="text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">{item.exposure}</span>
                </div>
                <span className={`font-semibold ${item.value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {item.value >= 0 ? '+' : ''}{item.value.toFixed(1)}M
                </span>
              </div>
            ))}
          </div>
          
          <div className="flex justify-between items-center text-xs mb-3 pt-2 border-t border-slate-100">
            <span className="text-slate-600">{language === 'pt' ? 'Realizado vs Não-Realizado' : 'Realized vs Unrealized'}</span>
            <span className="font-semibold text-primary">{finData.fxResult.realizedPct}% / {100 - finData.fxResult.realizedPct}%</span>
          </div>
          
          <div className="p-2.5 bg-amber-50/50 rounded-lg border border-amber-100">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-700 leading-relaxed">
                {language === 'pt' 
                  ? 'Resultado cambial adverso vs 2024 por desvalorização do AOA. Natureza volátil exige leitura separada do resultado financeiro recorrente. Exposição principal: dívida em USD.'
                  : 'Adverse FX result vs 2024 from AOA depreciation. Volatile nature requires separate reading from recurring financial result. Main exposure: USD debt.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BLOCK 4: Management Implications */}
      <div className="bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-serif text-lg font-bold text-primary mb-4">
          {language === 'pt' ? 'Implicações de Gestão' : 'Management Implications'}
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-red-600" />
              <h4 className="font-semibold text-sm text-slate-800">{language === 'pt' ? 'Financiamento' : 'Financing'}</h4>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'Custo bancário continua a ser o principal burden recorrente. Renegociação de spreads com CGA e Banco Valor pode gerar poupança de 0.2-0.3M/ano.'
                : 'Bank cost remains the main recurring burden. Spread renegotiation with CGA and Banco Valor could generate 0.2-0.3M/year savings.'}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <PiggyBank className="w-4 h-4 text-emerald-600" />
              <h4 className="font-semibold text-sm text-slate-800">{language === 'pt' ? 'Tesouraria' : 'Treasury'}</h4>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'Remuneração da caixa compensou parcialmente a pressão financeira. Oportunidade de melhorar yield com diversificação de instrumentos.'
                : 'Cash remuneration partially offset financial pressure. Opportunity to improve yield with instrument diversification.'}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-amber-600" />
              <h4 className="font-semibold text-sm text-slate-800">{language === 'pt' ? 'Câmbios' : 'FX'}</h4>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {language === 'pt' 
                ? 'Parte material do resultado financeiro continua explicada por factores voláteis. Aumentar hedge natural via matching de exposições pode reduzir volatilidade.'
                : 'Material part of financial result remains explained by volatile factors. Increasing natural hedge via exposure matching can reduce volatility.'}
            </p>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown Table - Quadro de Rubricas */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-[#0A192F] px-5 py-3">
          <h3 className="font-serif text-lg font-bold text-white">
            {language === 'pt' ? 'Resultados Financeiros' : 'Financial Results'}
          </h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0A192F] text-white">
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider">{language === 'pt' ? 'Descrição' : 'Description'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider">2025</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider">{language === 'pt' ? 'Orç. 2025' : 'Bdgt 2025'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. Abs.' : 'Var. Abs.'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. %' : 'Var. %'}</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">2024</th>
              <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}</th>
            </tr>
          </thead>
          <tbody>
            {/* Income Section */}
            <tr className="bg-emerald-50 border-b border-emerald-100">
              <td colSpan="7" className="py-2.5 px-4 font-semibold text-sm text-emerald-800">
                {language === 'pt' ? 'Proveitos e ganhos financeiros gerais' : 'General financial income and gains'}
              </td>
            </tr>
            {[
              { name: { pt: 'Juros de aplicações de tesouraria', en: 'Treasury application interest' }, v2025: 0.3, budget: 0.25, v2024: 0.2 },
              { name: { pt: 'Diferenças de câmbio favoráveis', en: 'Favorable exchange differences' }, v2025: 0.8, budget: 0.6, v2024: 1.2 },
            ].map((item, idx) => {
              const varAbs = item.v2025 - item.budget;
              const varPct = item.budget !== 0 ? ((item.v2025 - item.budget) / Math.abs(item.budget) * 100).toFixed(0) : 0;
              const varYoY = item.v2024 !== 0 ? ((item.v2025 - item.v2024) / Math.abs(item.v2024) * 100).toFixed(0) : 0;
              return (
              <tr key={`fin-gain-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-4 pl-8 text-slate-700">{item.name[language]}</td>
                <td className="py-2 px-3 text-right font-medium text-emerald-600 tabular-nums">{item.v2025.toFixed(1)}</td>
                <td className="py-2 px-3 text-right text-slate-500 tabular-nums">{item.budget.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varAbs.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varPct}%</td>
                <td className="py-2 px-3 text-right text-slate-400 tabular-nums">{item.v2024.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${parseFloat(varYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{parseFloat(varYoY) >= 0 ? '+' : ''}{varYoY}%</td>
              </tr>
            )})}
            <tr className="bg-emerald-50/50 border-b border-emerald-200">
              <td className="py-2 px-4 font-semibold text-emerald-800">{language === 'pt' ? 'Subtotal Proveitos' : 'Income Subtotal'}</td>
              <td className="py-2 px-3 text-right font-bold text-emerald-700 tabular-nums">1.1</td>
              <td className="py-2 px-3 text-right font-medium text-slate-600 tabular-nums">0.9</td>
              <td className="py-2 px-3 text-right font-bold text-emerald-600 tabular-nums">+0.2</td>
              <td className="py-2 px-3 text-right font-bold text-emerald-600 tabular-nums">+22%</td>
              <td className="py-2 px-3 text-right font-medium text-slate-400 tabular-nums">1.4</td>
              <td className="py-2 px-3 text-right font-bold text-red-600 tabular-nums">-21%</td>
            </tr>

            {/* Costs Section */}
            <tr className="bg-red-50 border-b border-red-100">
              <td colSpan="7" className="py-2.5 px-4 font-semibold text-sm text-red-800">
                {language === 'pt' ? 'Custos e perdas financeiras gerais' : 'General financial costs and losses'}
              </td>
            </tr>
            {[
              { name: { pt: 'Juros de empréstimos bancários', en: 'Bank loan interest' }, v2025: -3.2, budget: -3.0, v2024: -2.8 },
              { name: { pt: 'Diferenças de câmbios desfavoráveis', en: 'Unfavorable exchange differences' }, v2025: -2.6, budget: -1.5, v2024: -0.7 },
              { name: { pt: 'Custo financeiro (Unwind) - Provisão Ambiental', en: 'Financial cost (Unwind) - Environmental Provision' }, v2025: -0.6, budget: -0.5, v2024: -0.5 },
              { name: { pt: 'Comissão de estruturação', en: 'Structuring commission' }, v2025: -0.4, budget: -0.4, v2024: -0.3 },
              { name: { pt: 'Serviços bancários', en: 'Banking services' }, v2025: -0.2, budget: -0.2, v2024: -0.3 },
            ].map((item, idx) => {
              const varAbs = item.v2025 - item.budget;
              const varPct = item.budget !== 0 ? ((item.v2025 - item.budget) / Math.abs(item.budget) * 100).toFixed(0) : 0;
              const varYoY = item.v2024 !== 0 ? ((item.v2025 - item.v2024) / Math.abs(item.v2024) * 100).toFixed(0) : 0;
              return (
              <tr key={`fin-cost-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 px-4 pl-8 text-slate-700">{item.name[language]}</td>
                <td className="py-2 px-3 text-right font-medium text-red-600 tabular-nums">{item.v2025.toFixed(1)}</td>
                <td className="py-2 px-3 text-right text-slate-500 tabular-nums">{item.budget.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varAbs.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${varAbs >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{varAbs >= 0 ? '+' : ''}{varPct}%</td>
                <td className="py-2 px-3 text-right text-slate-400 tabular-nums">{item.v2024.toFixed(1)}</td>
                <td className={`py-2 px-3 text-right tabular-nums ${parseFloat(varYoY) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{parseFloat(varYoY) >= 0 ? '+' : ''}{varYoY}%</td>
              </tr>
            )})}
            <tr className="bg-red-50/50 border-b border-red-200">
              <td className="py-2 px-4 font-semibold text-red-800">{language === 'pt' ? 'Subtotal Custos' : 'Costs Subtotal'}</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">-7.0</td>
              <td className="py-2 px-3 text-right font-medium text-slate-600 tabular-nums">-5.6</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">-1.4</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">+25%</td>
              <td className="py-2 px-3 text-right font-medium text-slate-400 tabular-nums">-4.6</td>
              <td className="py-2 px-3 text-right font-bold text-red-700 tabular-nums">+52%</td>
            </tr>

            {/* Total */}
            <tr className="bg-gold-50/50">
              <td className="py-3 px-4 font-bold text-primary">{language === 'pt' ? 'TOTAL RESULTADOS FINANCEIROS' : 'TOTAL FINANCIAL RESULTS'}</td>
              <td className="py-3 px-3 text-right text-base font-bold text-primary tabular-nums">-5.9</td>
              <td className="py-3 px-3 text-right font-semibold text-slate-600 tabular-nums">-4.7</td>
              <td className="py-3 px-3 text-right text-base font-bold text-red-600 tabular-nums">-1.2</td>
              <td className="py-3 px-3 text-right text-base font-bold text-red-600 tabular-nums">+26%</td>
              <td className="py-3 px-3 text-right font-semibold text-slate-400 tabular-nums">-3.2</td>
              <td className="py-3 px-3 text-right text-base font-bold text-red-600 tabular-nums">+84%</td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default FinancialResults;
